// ** React Imports
import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

// ** MUI Imports
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableRow from '@mui/material/TableRow'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Backdrop from '@mui/material/Backdrop'
import Chip from '@mui/material/Chip'

// ** Redux Imports
import { useDispatch, useSelector } from 'react-redux'
import { fetchPricingbooksData, pricingBooksSelector } from 'store/metaData/pricingbook'

// ** 3rd Party Libraries
import moment from 'moment'

// Styled Card component
const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  border: `solid 1px ${theme.palette.grey[300]}`
}))

const Row = ({ row }) => {
  // ** Hooks
  const navigate = useNavigate()

  return (
    <TableRow
      hover
      onClick={() => navigate(`/pricing-books/salesforce-sync/${row.id}`)}
      sx={{ cursor: 'pointer' }}
    >
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', fontSize: '0.875rem' }}>
            {row.name}
          </Typography>
          {row.is_default && (
            <Chip
              label='Default'
              size='small'
              sx={{ backgroundColor: 'common.blue', color: 'common.white', fontWeight: 600 }}
            />
          )}
        </Box>
      </TableCell>
      <TableCell align='left'>{row.from ? moment(row.from).format('MMM DD, YYYY') : '------'}</TableCell>
      <TableCell align='left'>{row.to ? moment(row.to).format('MMM DD, YYYY') : '------'}</TableCell>
      <TableCell align='left'>{row.status || '------'}</TableCell>
    </TableRow>
  )
}

const SalesforceSync = () => {
  // ** Hooks
  const dispatch = useDispatch()

  // ** Selectors
  const { pricingBooksData, loading } = useSelector(pricingBooksSelector)

  useEffect(() => {
    dispatch(fetchPricingbooksData())
  }, [])

  const sortedPricingBooksData = [...(pricingBooksData || [])].sort(
    (a, b) => (b.is_default === true) - (a.is_default === true)
  )

  return (
    <StyledCard sx={{ position: 'relative' }}>
      <CardHeader title='Sync Price Books to Salesforce' />
      <Box sx={{ position: 'relative' }}>
        <TableContainer component={Paper}>
          <Table aria-label='salesforce sync table'>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align='left'>From</TableCell>
                <TableCell align='left'>To</TableCell>
                <TableCell align='left'>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedPricingBooksData.map(row => (
                <Row key={row.id} row={row} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Backdrop
          open={loading === 'GET'}
          sx={{
            position: 'absolute',
            color: theme => theme.palette.common.blue,
            zIndex: theme => theme.zIndex.mobileStepper - 1,
            '&.MuiBackdrop-root': {
              backgroundColor: 'common.white'
            }
          }}
        >
          <CircularProgress color='inherit' />
        </Backdrop>
      </Box>
    </StyledCard>
  )
}

export default SalesforceSync
